import React, { useEffect, useMemo, useRef, useState } from "react";
import { CaseData, CaseStatus } from "@kahunmedical/common-wtypes";
import {
  IconButton,
  Link,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { isRtl } from "@kahunmedical/common-configuration";
import {
  inProgress,
  usePatientSummary,
} from "fe-common/patientSummary/useFetchPatientSummary";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { EnhancedTableColumn } from "fe-common/ui/table/enhancedTable";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { PatientNotes } from "./patientNotes";
import { ClinicColors } from "../styles/clinicTheme";
import { VerticalDivider } from "./verticalDivider";
import { LoadingProgress } from "../loader/loadingProgress";
import { ReactComponent as ValidatedIcon } from "../assets/patientCard/validatedIcon.svg";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { PrivatePlaceholder } from "fe-web-components/patientCard/PrivatePlaceholder";
import { useClinicState } from "ask/src/clinic/clinicStateContext";
import { MedicalDataDisplay } from "./MedicalDataDisplay";
import RagSessionProvider from "rag-chat/src/context/sessionProvider";
import { EventReporterContextProvider as DialogueEventReporterProvider } from "rag-chat/src/analytics/EventReporterContext";
import { PatientCardDialogue } from "./patientCardDialogue";
import { summaryToText } from "./patientSummary";
import { dialogueReporter } from "rag-chat/src/analytics/dialogueReporter";

//region [[ Styles ]]
const PatientExpandedView = styled(Paper)({
  position: "relative",
  padding: "14px",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  "& td": {
    fontSize: "13px",
  },
});

const PatientCardView = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  ...scrollableStyle,
  "& .last-column": {
    width: "28%",
    minWidth: "28%",
  },
});

const PatientCardBottom = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "start",
  gap: 18,
  padding: "20px 0 4px",
  borderTop: "1px solid " + ClinicColors.borders,
  "& .bottom-section-item": {
    padding: "4px 0",
  },
  "& h3": { marginTop: 0, marginBottom: 12 },
  "& .physician-notes": {
    flex: 1,
  },
  "& .contact-details": {
    fontSize: 13,
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
    gap: 12,
  },
});

const MedicalDataView = styled("div")({
  display: "flex",
  flex: 1,
  flexDirection: "row",
  gap: 18,
  alignItems: "start",
});

const PollingMessage = styled("div")({
  backgroundColor: "#FFFFFF",
  padding: 16,
  border: `1px solid ${ClinicColors.borders}`,
  borderRadius: 10,
  position: "fixed",
  right: 50,
  bottom: 32,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientExpandedProps {
  data: CaseData;
  onSaveComment: (digitCode: string, comment: string) => any;
  onClose?: () => void;
  columns: EnhancedTableColumn[];
  onPatientMenuOpen: (event: any, patientData: CaseData) => void;
  LLMPolling?: boolean;
  startWalkthrough?: () => void;
}

export const PatientCard = ({ data, ...props }: PatientExpandedProps) => {
  const [showLLMingIndicator, setShowLLMingIndicator] = useState(true);
  const { translate } = useTranslateByGender();
  const [
    {
      clinicData: { name: clinicName, id: clinicId },
      clinicConfig,
      privateMode,
    },
  ] = useClinicState();

  const showDD = data?.visitReason?.id === undefined || !!data.chiefComplaint; //only show DD cases with chief complaint or without reason for visit
  const rtlDisplay = isRtl(clinicConfig.defaultLocale);
  const dialogueEnabled = clinicConfig?.dialogue?.enabled;

  const { caseId, digitCode, comment, patient } = data;
  const {
    loading,
    polling,
    possibleCauses,
    error,
    summary,
    assessment,
    workupActionItems,
    workup,
    caseStatus,
    getSummary,
    ddReady,
    assessmentGroundingReady,
  } = usePatientSummary(
    caseId,
    clinicId,
    undefined,
    undefined,
    showDD && clinicConfig?.[ClinicConfigKeys.UsesLLM], //use LLM polling
    clinicConfig?.[ClinicConfigKeys.UsesRag],
    clinicConfig?.[ClinicConfigKeys.GroundAssessment],
    clinicConfig?.[ClinicConfigKeys.GroundExplanation],
  );

  const caseIdRef = useRef(null);
  const changedId = caseId !== caseIdRef.current; //indicate case id change before the context was updated to overcome the delay in updating the display
  const useRag = showDD && clinicConfig?.[ClinicConfigKeys.UsesRag];
  const handleSummaryUpdate = (etag: string) => {
    //used after updating the summary for checking if the latest etag is the correct one
    getSummary(etag);
  };

  useEffect(() => {
    if (loading) {
      caseIdRef.current = caseId;
      setShowLLMingIndicator(inProgress(caseStatus) || inProgress(data.status));
    }
  }, [loading, caseId, caseStatus, data.status]);

  useEffect(() => {
    if (props.startWalkthrough && ddReady) {
      props.startWalkthrough(); //start walkthrough from the beginning.
    }
  }, [ddReady]);

  useEffect(() => {
    if (props.startWalkthrough && assessmentGroundingReady)
      props.startWalkthrough(); //start walkthrough from the grounding step
  }, [assessmentGroundingReady]);

  const EmbeddedDialogue = useMemo(() => {
    return (
      <PatientCardDialogue
        loading={changedId || !summary}
        rtlDisplay={rtlDisplay}
      />
    );
  }, [rtlDisplay, summary, changedId]);

  const topRowData: CaseData = {
    ...data,
    status: (caseStatus as CaseStatus) || data.status,
  };

  const dialogueEventReporter = useMemo(
    () => dialogueReporter("Standalone"),
    [],
  );

  return (
    <PatientExpandedView className={"patient-expanded-view"}>
      <Table>
        <TableBody>
          <TableRow>
            {props.columns
              .filter((c) => !c.hidden)
              .map((column, index) => (
                <TableCell key={`patientCell${index}`}>
                  {column.render
                    ? column.render(topRowData)
                    : topRowData[column.id]}
                </TableCell>
              ))}
            <TableCell align={"right"}>
              <IconButton
                size={"small"}
                onClick={(e) => {
                  props.onPatientMenuOpen(e, topRowData);
                }}
              >
                <MoreHorizIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <PatientCardView
        className={`patient-expanded-card${rtlDisplay ? " rtl-view" : ""}`}
        dir={rtlDisplay ? "rtl" : "ltr"}
      >
        <MedicalDataView
          className={"medical-data-view"}
          style={dialogueEnabled ? { height: "100%" } : null}
        >
          <DialogueEventReporterProvider eventReporter={dialogueEventReporter}>
            <RagSessionProvider
              active={useRag || dialogueEnabled}
              embedded={true}
              rtlDisplay={rtlDisplay}
              caseId={caseId}
              summary={summary ? summaryToText(summary) : ""}
              app={clinicConfig?.dialogue?.dialogueApp}
              showSuggestedQuestions={
                clinicConfig?.dialogue?.showSuggestedQuestions
              }
            >
              <MedicalDataDisplay
                caseId={caseId}
                error={error}
                loadingSummary={!summary}
                loadingDD={polling?.dd}
                loadingAssessment={polling?.assessment}
                loadingWorkup={polling?.workup}
                showDD={showDD}
                summary={summary}
                possibleCauses={possibleCauses}
                assessment={assessment}
                workup={workup}
                workupActionItems={workupActionItems}
                caseStatus={caseStatus as CaseStatus}
                onUpdate={handleSummaryUpdate}
                displayMode={dialogueEnabled ? "tabs" : "columns"}
                loading={changedId}
              />
              {dialogueEnabled && (
                <>
                  <VerticalDivider />
                  {EmbeddedDialogue}
                </>
              )}
            </RagSessionProvider>
          </DialogueEventReporterProvider>
        </MedicalDataView>

        {//TODO: add a clinic feature flag for showing notes and contact details (instead of checking dialogueEnabled)
        !dialogueEnabled && (
          <PatientCardBottom>
            <div className="physician-notes bottom-section-item">
              <h3>{translate("PHYSICIAN_NOTES")}</h3>
              <PatientNotes
                comment={comment}
                onSaveComment={props.onSaveComment}
                digitCode={digitCode}
                translate={translate}
                clinicName={clinicName}
                caseId={caseId}
              />
            </div>
            <VerticalDivider />
            <div className="bottom-section-item last-column">
              <h3>{translate("CONTACT_DETAILS")}</h3>
              <div className={"contact-details"}>
                {privateMode ? (
                  <PrivatePlaceholder />
                ) : (
                  <>
                    {!patient.contactDetails?.phone &&
                      !patient.contactDetails?.email &&
                      translate("NONE_DEFINED")}
                    {patient.contactDetails?.phone && (
                      <div>
                        {translate("PHONE_NUMBER")}
                        <br />
                        <Link
                          href={"tel:" + patient.contactDetails.phone}
                          dir="ltr"
                        >
                          {patient.contactDetails.phone}
                        </Link>
                      </div>
                    )}
                    {patient.contactDetails?.email && (
                      <div>
                        {translate("EMAIL")}
                        <br />
                        <Link href={"mailto:" + patient.contactDetails.email}>
                          {patient.contactDetails.email}
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </PatientCardBottom>
        )}
      </PatientCardView>
      {showLLMingIndicator && (
        <>
          {polling?.dd || polling?.assessment || polling?.workup ? (
            <PollingMessage>
              <LoadingProgress label={"Generating assessment..."} />
            </PollingMessage>
          ) : (
            (polling?.groundingAssessment || polling?.groundingExplanation) && (
              <PollingMessage>
                <LoadingProgress
                  label={"Grounding in progress"}
                  icon={<ValidatedIcon />}
                  onClose={() => setShowLLMingIndicator(false)}
                />
              </PollingMessage>
            )
          )}
        </>
      )}
    </PatientExpandedView>
  );
};
